import axios from 'axios';

const DEFAULT_BACKEND_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8000'
  : 'https://convoyageos.onrender.com';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 35000,
});

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('role');
  localStorage.removeItem('user_id');
};

const decodeJwtPayload = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    if (!base64Payload) return null;
    const normalized = base64Payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const isTokenExpiredOrNearExpiry = (token, thresholdSeconds = 30) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= (now + thresholdSeconds);
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  refreshPromise = axios.post(`${API_BASE_URL}/api/auth/refresh`, {
    refresh_token: refreshToken,
  }).then((refreshResponse) => {
    const newAccessToken = refreshResponse.data.access_token;
    localStorage.setItem('token', newAccessToken);
    if (refreshResponse.data.refresh_token) {
      localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
    }
    return newAccessToken;
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

api.interceptors.request.use(async (config) => {
  const isRefreshCall = (config.url || '').includes('/api/auth/refresh');
  if (isRefreshCall) {
    return config;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    return config;
  }

  if (isTokenExpiredOrNearExpiry(token)) {
    try {
      const refreshedToken = await refreshAccessToken();
      config.headers.Authorization = `Bearer ${refreshedToken}`;
      return config;
    } catch {
      clearStoredAuth();
      window.dispatchEvent(new Event('auth:force-logout'));
      return config;
    }
  }

  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const isUnauthorized = error.response?.status === 401;
    const isRefreshCall = (originalRequest.url || '').includes('/api/auth/refresh');
    const isAuthCall = (originalRequest.url || '').match(/\/api\/auth\/(login|register)/);

    if (!isUnauthorized || isRefreshCall || isAuthCall || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch {
      clearStoredAuth();
      window.dispatchEvent(new Event('auth:force-logout'));
      return Promise.reject(error);
    }
  }
);

export default api;
